<template>
  <div class="multi-badge inline-block">
    <div
      v-bind="$attrs"
      class="items-center rounded-md px-1.5 py-0.5 font-medium select-none"
      :class="[getSizeClass(), colorClass]"
    >
      <slot />
      <button
        v-if="removable"
        type="button"
        @click.stop.prevent="$emit('remove')"
        class="ml-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full focus:outline-hidden"
      >
        <FontAwesomeIcon :icon="['fas', 'trash-can']" size="xs" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"

defineEmits(["remove"])

defineOptions({
  inheritAttrs: false
})

type BadgeSize = "xs" | "sm" | "md" | "lg"

const props = defineProps({
  size: {
    type: String as () => BadgeSize,
    default: "md",
    validator: (value: string) => ["lg", "md", "sm", "xs"].includes(value)
  },
  removable: {
    type: Boolean,
    default: false
  },
  fileUrl: {
    type: String,
    default: null
  },
  color: {
    type: String,
    default: "primary",
    validator: (value: string) => {
      return [
        "primary",
        "primary-lighten-1",
        "secondary",
        "surface",
        "warning",
        "error",
        "success"
      ].includes(value)
    }
  }
})

const backgroundClasses: Record<string, string> = {
  primary: "bg-primary dark:bg-primary-dark",
  "primary-lighten-1": "bg-primary-lighten-1 dark:bg-primary-lighten-1-dark",
  secondary: "bg-secondary dark:bg-secondary-dark",
  surface: "bg-surface dark:bg-surface-dark",
  warning: "bg-warning dark:bg-warning-dark",
  error: "bg-error dark:bg-error-dark",
  success: "bg-success dark:bg-success-dark"
}

const textOnBackgroundClasses: Record<string, string> = {
  primary: "text-on-primary dark:text-on-primary-dark",
  "primary-lighten-1": "text-on-primary-lighten-1 dark:text-on-primary-lighten-1-dark",
  secondary: "text-on-secondary dark:text-on-secondary-dark",
  surface: "text-on-surface dark:text-on-surface-dark",
  warning: "text-on-warning dark:text-on-warning-dark",
  error: "text-on-error dark:text-on-error-dark",
  success: "text-on-success dark:text-on-success-dark"
}

const colorClass = computed(() => {
  const baseColorClass = props.color || "primary"

  return `${backgroundClasses[baseColorClass]} ${textOnBackgroundClasses[baseColorClass]}`
})

function getSizeClass() {
  const textSize = {
    xs: "text-xs",
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg"
  }

  return textSize[props.size]
}
</script>

<style scoped></style>
